import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Brand from "../components/Brand";

/**
 * Tempo first-run experience — two-column onboarding.
 *
 * Left  → editorial: wordmark, tagline, rhythm divider, large headline,
 *         supporting copy, primary CTA + secondary "Sign in" link.
 * Right → elevated setup card: three fields + Continue.
 *
 * Fits on one desktop screen. Stacks cleanly on mobile.
 * Uses Tempo's existing design tokens exclusively.
 */

// Rhythm motif removed from onboarding — kept space intentional and quiet.

export default function Setup() {
    const { needsSetup, completeSetup } = useAuth();
    const { updateSettings } = useSettings();
    const navigate = useNavigate();

    const nameInputRef = useRef(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [studio, setStudio] = useState("");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!needsSetup) navigate("/", { replace: true });
    }, [needsSetup, navigate]);

    const submit = (e) => {
        e.preventDefault();
        const errs = {};
        if (!name.trim()) errs.name = "Required";
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            nameInputRef.current?.focus();
            toast.error("Please enter your name to continue");
            return;
        }
        try {
            completeSetup({
                admin: { name, email },
                firstTeacher: null,
            });
            if (studio.trim()) updateSettings?.({ studioName: studio.trim() });
            toast.success(`Welcome, ${name.trim().split(/\s+/)[0]}`);
            navigate("/", { replace: true });
        } catch (err) {
            toast.error(err.message || "Could not complete setup");
        }
    };

    const focusForm = () => nameInputRef.current?.focus();

    const softLabel = "text-sm font-medium tracking-normal text-ink";
    const softInput =
        "h-12 rounded-xl bg-white border-mist focus-visible:ring-action focus-visible:ring-offset-0 text-base placeholder:text-ink-mute/70";

    return (
        <div
            className="min-h-screen antialiased text-ink"
            style={{ background: "#F8F7F4" }}
            data-testid="setup-page"
        >
            <div className="min-h-screen mx-auto max-w-[1200px] px-6 sm:px-10 py-10 flex items-center">
                <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* --------------------------------- LEFT --------- */}
                    <section
                        className="flex flex-col"
                        style={{ animation: "fadeUp 500ms ease-out both" }}
                        data-testid="setup-left"
                    >
                        <div style={{ marginLeft: "-52px" }}>
                            <Brand
                                variant="wordmark"
                                size="xl"
                                to={null}
                                testId="setup-brand"
                            />
                        </div>
                        <p
                            className="mt-1 text-[19px] font-normal tracking-normal"
                            style={{ color: "#4A6F8F" }}
                            data-testid="setup-tagline"
                        >
                            Keep every lesson in rhythm.
                        </p>

                        <h1
                            className="mt-12 font-display font-bold text-[64px] sm:text-[84px] lg:text-[92px] leading-[1.02] tracking-[-0.02em] text-ink"
                            data-testid="setup-headline"
                        >
                            For those
                            <br />
                            who teach
                            <br />
                            <span style={{ color: "#4A6F8F" }}>every day.</span>
                        </h1>

                        <p className="mt-10 text-lg text-ink-soft leading-relaxed max-w-md">
                            Attendance, schedules, students, and progress.
                            <br />
                            Everything you need to stay in rhythm.
                        </p>

                        <div className="mt-10 flex items-center gap-6 flex-wrap">
                            <Button
                                type="submit"
                                form="setup-form"
                                className="focus-ring rounded-xl bg-action hover:bg-action-hover text-white font-medium text-base px-7 shadow-soft"
                                style={{ height: 56 }}
                                data-testid="setup-continue-left"
                            >
                                Continue
                                <ArrowRight
                                    size={16}
                                    strokeWidth={2.4}
                                    className="ml-2"
                                />
                            </Button>
                            <div className="text-sm">
                                <div className="text-ink-soft">
                                    Already have an account?
                                </div>
                                <button
                                    type="button"
                                    onClick={focusForm}
                                    className="focus-ring inline-flex items-center gap-1 text-action font-medium hover:text-action-hover transition-colors"
                                    data-testid="setup-signin"
                                >
                                    Sign in
                                    <ArrowRight
                                        size={14}
                                        strokeWidth={2.4}
                                    />
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* --------------------------------- RIGHT -------- */}
                    <section
                        className="flex flex-col items-center lg:items-stretch"
                        style={{ animation: "fadeUp 600ms ease-out 120ms both" }}
                        data-testid="setup-right"
                    >
                        <form
                            id="setup-form"
                            onSubmit={submit}
                            className="w-full max-w-[560px] bg-white rounded-3xl p-8 sm:p-10 space-y-6"
                            style={{
                                boxShadow: "0 24px 60px rgba(32, 53, 57, 0.08), 0 6px 18px rgba(32, 53, 57, 0.04)",
                            }}
                            data-testid="setup-form"
                        >
                            <div className="space-y-2">
                                <h2
                                    className="font-display font-bold text-4xl sm:text-[42px] tracking-tight text-ink leading-tight"
                                    data-testid="setup-form-heading"
                                >
                                    Let&apos;s get you started.
                                </h2>
                                <p className="text-[15px] text-ink-soft leading-relaxed">
                                    Create your admin profile to begin.
                                    <br />
                                    You can always add teachers and students later.
                                </p>
                            </div>

                            <div className="pt-2 space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="setup-name" className={softLabel}>
                                        Your name
                                    </Label>
                                    <Input
                                        id="setup-name"
                                        ref={nameInputRef}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g. Maya Ortiz"
                                        className={softInput}
                                        data-testid="setup-admin-name"
                                        autoFocus
                                    />
                                    {errors.name && (
                                        <div className="text-xs font-medium text-danger">
                                            {errors.name}
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="setup-email" className={softLabel}>
                                        Email
                                    </Label>
                                    <Input
                                        id="setup-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="e.g. maya@studio.com"
                                        className={softInput}
                                        data-testid="setup-admin-email"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="setup-studio" className={softLabel}>
                                        Studio name
                                    </Label>
                                    <Input
                                        id="setup-studio"
                                        value={studio}
                                        onChange={(e) => setStudio(e.target.value)}
                                        placeholder="e.g. Harmony Music Studio"
                                        className={softInput}
                                        data-testid="setup-studio-name"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="focus-ring w-full mt-4 rounded-xl bg-action hover:bg-action-hover text-white font-medium text-base"
                                style={{ height: 56 }}
                                data-testid="setup-submit-btn"
                            >
                                Continue
                                <ArrowRight
                                    size={16}
                                    strokeWidth={2.4}
                                    className="ml-2"
                                />
                            </Button>

                            <p className="text-center text-xs text-ink-mute pt-1">
                                You can change these details anytime.
                            </p>
                        </form>
                    </section>
                </div>
            </div>
        </div>
    );
}
